import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import MediaCarousel from "@/pages/event/components/MediaCarousel";
import type { MediaItem } from "@/pages/event/components/MediaCarousel";
import MarcaEntreElas from "@/components/feature/MarcaEntreElas";

interface EventDetail {
  id: number;
  slug: string;
  title: string;
  caption: string;
  date: string;
  location: string;
  main_image: string;
  description: string;
  full_content: string;
  gallery_images: string[];
  gallery_videos: string[];
  created_at: string;
}

function getYouTubeEmbedUrl(url: string): string | null {
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s]+)/);
  return match ? `https://www.youtube.com/embed/${match[1]}` : null;
}

function getVimeoEmbedUrl(url: string): string | null {
  const match = url.match(/vimeo\.com\/(\d+)/);
  return match ? `https://player.vimeo.com/video/${match[1]}` : null;
}

export default function EventPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [event, setEvent] = useState<EventDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxMediaType, setLightboxMediaType] = useState<'photos' | 'videos'>('photos');

  const [carouselVisible, setCarouselVisible] = useState(1);

  // Inject JSON-LD structured data for AI / search engines
  useEffect(() => {
    if (!event) return;

    const existingScript = document.getElementById("event-detail-jsonld");
    if (existingScript) existingScript.remove();

    const script = document.createElement("script");
    script.id = "event-detail-jsonld";
    script.type = "application/ld+json";

    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "Event",
      "name": event.title,
      "description": event.description,
      "image": event.main_image,
      "startDate": event.date || undefined,
      "location": event.location ? {
        "@type": "Place",
        "name": event.location,
      } : undefined,
      "url": `https://entreelas.com.br/evento/${event.slug}`,
    };

    script.textContent = JSON.stringify(jsonLd);
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById("event-detail-jsonld");
      if (el) el.remove();
    };
  }, [event]);

  useEffect(() => {
    function updateVisible() {
      if (window.innerWidth >= 768) {
        setCarouselVisible(3);
      } else if (window.innerWidth >= 640) {
        setCarouselVisible(2);
      } else {
        setCarouselVisible(1);
      }
    }
    updateVisible();
    window.addEventListener("resize", updateVisible);
    return () => window.removeEventListener("resize", updateVisible);
  }, []);

  useEffect(() => {
    async function load() {
      if (!slug) {
        setError(true);
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const { data, error: dbError } = await supabase
          .from("events")
          .select("id, slug, title, caption, date, location, main_image, description, full_content, gallery_images, gallery_videos, created_at")
          .eq("slug", slug)
          .eq("status", "active")
          .maybeSingle();

        if (dbError || !data) {
          setError(true);
        } else {
          setEvent(data);
        }
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [slug]);

  function getPhotoItems(ev: EventDetail): MediaItem[] {
    const items: MediaItem[] = [];
    items.push({ type: 'image', url: ev.main_image });
    (ev.gallery_images || []).forEach((url) => items.push({ type: 'image', url }));
    return items;
  }

  function getVideoItems(ev: EventDetail): MediaItem[] {
    return (ev.gallery_videos || []).map((url) => ({ type: 'video', url }));
  }

  function getLightboxItems(): MediaItem[] {
    if (!event) return [];
    return lightboxMediaType === 'photos' ? getPhotoItems(event) : getVideoItems(event);
  }

  function openPhotoLightbox(index: number) {
    setLightboxMediaType('photos');
    setLightboxIndex(index);
    setLightboxOpen(true);
  }

  function openVideoLightbox(index: number) {
    setLightboxMediaType('videos');
    setLightboxIndex(index);
    setLightboxOpen(true);
  }

  function closeLightbox() {
    setLightboxOpen(false);
  }

  function nextLightbox() {
    const items = getLightboxItems();
    if (items.length === 0) return;
    setLightboxIndex((prev) => (prev + 1) % items.length);
  }

  function prevLightbox() {
    const items = getLightboxItems();
    if (items.length === 0) return;
    setLightboxIndex((prev) => (prev - 1 + items.length) % items.length);
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <div className="sticky top-0 z-50 w-full bg-white/90 backdrop-blur-md border-b border-nude/30">
          <div className="w-full px-6 md:px-10 lg:px-16 h-16 flex items-center justify-between">
            <MarcaEntreElas />
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <div className="sticky top-0 z-50 w-full bg-white/90 backdrop-blur-md border-b border-nude/30">
          <div className="w-full px-6 md:px-10 lg:px-16 h-16 flex items-center justify-between">
            <MarcaEntreElas />
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-beige mx-auto mb-4">
              <i className="ri-calendar-close-line text-2xl text-brown/50" />
            </div>
            <h2 className="font-display text-xl text-brown mb-2">
              Evento não encontrado
            </h2>
            <p className="font-body text-brown/70 text-sm mb-6">
              O evento que você procura não existe ou não está mais disponível.
            </p>
            <button
              onClick={() => navigate("/")}
              className="inline-block font-body text-sm px-6 py-3 rounded-full bg-primary text-white tracking-wide transition-all hover:bg-primary/90 cursor-pointer"
            >
              Voltar para a página inicial
            </button>
          </div>
        </div>
      </div>
    );
  }

  const photoItems = getPhotoItems(event);
  const videoItems = getVideoItems(event);
  const hasPhotos = photoItems.length > 0;
  const hasVideos = videoItems.length > 0;
  const lightboxItems = getLightboxItems();

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      <div className="sticky top-0 z-50 w-full bg-white/90 backdrop-blur-md border-b border-nude/30">
        <div className="w-full px-6 md:px-10 lg:px-16 h-16 flex items-center justify-between">
          <MarcaEntreElas />
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-beige text-brown hover:bg-primary hover:text-white transition-all duration-300 cursor-pointer"
            aria-label="Voltar"
          >
            <i className="ri-arrow-left-line text-lg" />
          </button>
        </div>
      </div>

      {/* Hero Image */}
      <div className="w-full h-[300px] md:h-[450px] overflow-hidden relative bg-brown">
        {/* A foto aparece inteira (contain) sobre uma cópia desfocada dela mesma: foto de grupo deitada não perde
            as pessoas para o teto, e foto em pé não perde as cabeças, em qualquer largura de tela. */}
        <img
          src={event.main_image}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-70"
        />
        <img
          src={event.main_image}
          alt={event.title}
          className="relative w-full h-full object-contain"
        />
      </div>

      {/* Content */}
      <div className="w-full px-6 md:px-10 lg:px-16 pt-10 md:pt-14 pb-12 md:pb-16">
        <div className="max-w-4xl mx-auto">
          {/* Título logo abaixo da foto, centralizado (antes ficava por cima da foto, desalinhado com ela) */}
          {/* Estilo "Selo da marca" (10/14): "Encontro" em cursiva rosé e o resto em letra de inscrição, filete com coração */}
          {(() => {
            const m = event.title.match(/^Encontro\s+(.+)$/i);
            return (
              <div className="text-center mb-8">
                <p className="font-['Montserrat'] font-medium text-[11px] tracking-[0.38em] uppercase text-[#B9775E]">
                  Nossa História
                </p>
                {m && (
                  <p className="font-['Great_Vibes'] text-4xl md:text-5xl text-[#B9775E] leading-none mt-4">Encontro</p>
                )}
                <h1 className={`font-['Cinzel'] font-medium uppercase text-brown tracking-[0.12em] leading-tight text-[26px] md:text-4xl lg:text-5xl ${m ? "mt-1" : "mt-4"}`}>
                  {m ? m[1] : event.title}
                </h1>
                <div className="flex items-center justify-center gap-3.5 mt-5 text-[#B9775E]" aria-hidden="true">
                  <span className="w-16 md:w-24 h-px bg-current opacity-60" />
                  <i className="ri-heart-fill text-xs" />
                  <span className="w-16 md:w-24 h-px bg-current opacity-60" />
                </div>
              </div>
            );
          })()}

          {/* Meta info */}
          <div className="flex flex-wrap justify-center gap-3 md:gap-4 mb-8 pb-8 border-b border-nude/50">
            {event.date && (
              <div className="flex items-center gap-2 bg-beige/60 px-4 py-2 rounded-full">
                <i className="ri-calendar-line text-primary text-sm" />
                <span className="font-body text-sm text-brown">{event.date}</span>
              </div>
            )}
            {event.location && (
              <div className="flex items-center gap-2 bg-beige/60 px-4 py-2 rounded-full">
                <i className="ri-map-pin-line text-primary text-sm" />
                <span className="font-body text-sm text-brown">{event.location}</span>
              </div>
            )}
            {/* A legenda repete local e data ("World of Brigadeiro — 10 de outubro de 2026, 5pm"): aqui só o horário */}
            {(() => {
              const hora = event.caption?.match(/,\s*([^,]+)$/)?.[1];
              return (
                <div className="flex items-center gap-2 bg-beige/60 px-4 py-2 rounded-full">
                  <i className={`${hora ? "ri-time-line" : "ri-eye-line"} text-primary text-sm`} />
                  <span className="font-body text-sm text-brown">{hora || event.caption}</span>
                </div>
              );
            })()}
          </div>

          {/* Description */}
          <p className="font-body text-lg md:text-xl text-brown/80 leading-relaxed mb-10">
            {event.description}
          </p>

          {/* Full Content */}
          <div className="prose prose-brown max-w-none">
            {event.full_content?.split("\n\n").map((paragraph, i) => (
              <p key={i} className="font-body text-base text-brown/75 leading-relaxed mb-6">
                {paragraph}
              </p>
            ))}
          </div>
        </div>
      </div>

      {/* Fotos Section */}
      {hasPhotos && (
        <div className="w-full px-6 md:px-10 lg:px-16 py-12 md:py-16 bg-beige/40">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 md:mb-8">
              <div>
                <p className="font-body text-xs tracking-[0.25em] uppercase text-primary mb-2">
                  Galeria
                </p>
                <h2 className="font-display text-xl md:text-2xl text-brown">
                  Fotos
                </h2>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => openPhotoLightbox(0)}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-primary text-white font-body text-sm hover:bg-primary/90 transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-apps-line" />
                  Ver todas
                </button>
              </div>
            </div>

            <MediaCarousel
              items={photoItems}
              carouselVisible={carouselVisible}
              onItemClick={openPhotoLightbox}
              isPaused={lightboxOpen}
            />
          </div>
        </div>
      )}

      {/* Vídeos Section */}
      {hasVideos && (
        <div className="w-full px-6 md:px-10 lg:px-16 py-12 md:py-16 bg-white">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 md:mb-8">
              <div>
                <p className="font-body text-xs tracking-[0.25em] uppercase text-primary mb-2">
                  Galeria
                </p>
                <h2 className="font-display text-xl md:text-2xl text-brown">
                  Vídeos
                </h2>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => openVideoLightbox(0)}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-primary text-white font-body text-sm hover:bg-primary/90 transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-apps-line" />
                  Ver todos
                </button>
              </div>
            </div>

            <MediaCarousel
              items={videoItems}
              carouselVisible={carouselVisible}
              onItemClick={openVideoLightbox}
              isPaused={lightboxOpen}
            />
          </div>
        </div>
      )}

      {/* CTA Back */}
      <div className="w-full px-6 md:px-10 lg:px-16 py-12 md:py-16">
        <div className="max-w-4xl mx-auto">
          <div className="pt-8 border-t border-nude/50">
            <Link
              to="/"
              className="inline-flex items-center gap-2 font-body text-sm text-primary hover:text-brown transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line" />
              <span>Voltar para a página inicial</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxOpen && lightboxItems.length > 0 && (
        <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center" onClick={closeLightbox}>
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 w-12 h-12 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors cursor-pointer z-10"
          >
            <i className="ri-close-line text-2xl" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); prevLightbox(); }}
            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors cursor-pointer z-10"
          >
            <i className="ri-arrow-left-line text-2xl" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); nextLightbox(); }}
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors cursor-pointer z-10"
          >
            <i className="ri-arrow-right-line text-2xl" />
          </button>

          <div className="px-8 py-4 max-w-5xl max-h-[85vh] w-full flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            {lightboxItems[lightboxIndex].type === 'video' ? (
              <div className="w-full max-w-4xl aspect-video rounded-lg overflow-hidden bg-black">
                {getYouTubeEmbedUrl(lightboxItems[lightboxIndex].url) ? (
                  <iframe
                    src={getYouTubeEmbedUrl(lightboxItems[lightboxIndex].url)!}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title={`Vídeo ${lightboxIndex + 1}`}
                  />
                ) : getVimeoEmbedUrl(lightboxItems[lightboxIndex].url) ? (
                  <iframe
                    src={getVimeoEmbedUrl(lightboxItems[lightboxIndex].url)!}
                    className="w-full h-full"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                    title={`Vídeo ${lightboxIndex + 1}`}
                  />
                ) : (
                  <video
                    src={lightboxItems[lightboxIndex].url}
                    className="w-full h-full object-contain"
                    controls
                    autoPlay
                    playsInline
                  />
                )}
              </div>
            ) : (
              <img
                src={lightboxItems[lightboxIndex].url}
                alt={`Foto ${lightboxIndex + 1} de ${event.title}`}
                className="max-w-full max-h-[75vh] object-contain rounded-lg"
              />
            )}
            <p className="text-center text-white/70 font-body text-sm mt-4">
              {lightboxIndex + 1} / {lightboxItems.length} — {event.title}
              {lightboxItems[lightboxIndex].type === 'video' && (
                <span className="ml-2 inline-flex items-center gap-1">
                  <i className="ri-video-line" /> Vídeo
                </span>
              )}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}